import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CachedBuildingService, DeleteLocalCacheCategory, ENVIRONMENT, SharedService } from '@cfp/core';
import { Alert, CfpTranslateModule } from '@cfp/ui';
import { BuildingNotificationService, BuildingNotificationTypeDTO } from 'csr-asset-app';
import { combineLatest, filter, map, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'cfp-building-notification',
  templateUrl: './building-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Alert, CfpTranslateModule]
})
export class BuildingNotificationComponent {
  public readonly buildingId = input.required<number>();
  public readonly type = input.required<BuildingNotificationTypeDTO>();

  private readonly environment = inject(ENVIRONMENT);
  private readonly buildingNotificationService = inject(BuildingNotificationService);
  private readonly cachedBuildingService = inject(CachedBuildingService);
  private readonly sharedService = inject(SharedService);

  private reloadTrigger = this.sharedService.deleteLocalCacheObservable.pipe(
    map(() => new Date().getTime()) // Force a new value on each emission
  );

  protected readonly shouldShow = toSignal(
    combineLatest([
      toObservable(this.buildingId),
      toObservable(this.type),
      this.reloadTrigger.pipe(startWith(null))
    ]).pipe(
      filter(
        ([buildingId, type]) =>
          buildingId > 0 &&
          this.environment.showBuildingNotifications &&
          ((typeof this.environment.showBuildingNotifications === 'boolean' &&
            this.environment.showBuildingNotifications === true) ||
            (this.environment.showBuildingNotifications as BuildingNotificationTypeDTO[]).includes(type))
      ),
      switchMap(([buildingId, type]) =>
        this.cachedBuildingService
          .v2BuildingsBuildingIdNotificationsGet(buildingId)
          .pipe(map((notificationsToShow) => notificationsToShow.includes(type)))
      )
    ),
    { initialValue: false }
  );

  protected dismiss(): void {
    this.buildingNotificationService
      .v2BuildingsBuildingIdNotificationsBuildingNotificationTypeDismissPut(this.buildingId(), this.type())
      .subscribe({
        next: () => {
          this.sharedService.emitDeleteLocalCache(DeleteLocalCacheCategory.Notification);
        },
        error: () => {
          // Fail silently
        }
      });
  }
}
